import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Images } from '../assets/assets';
import ReactChart from 'echarts-for-react';
import scanService from '../services/scanService';
import './Biomarkers.css';

// Enhanced Biomarkers Component
function BiomarkersDisplay({ scanData, allScansData }) {
  const tooltipRef = useRef(null);
  const lesionTooltipRef = useRef(null);

  // Tooltip functions
  const showTooltip = (event, data) => {
    if (tooltipRef.current) {
      const tooltip = tooltipRef.current;
      const timepointEl = tooltip.querySelector('.tooltip-timepoint');
      const dateEl = tooltip.querySelector('.tooltip-date');
      const countEl = tooltip.querySelector('.tooltip-count');
      const volumeEl = tooltip.querySelector('.tooltip-volume');

      if (timepointEl) timepointEl.textContent = data.timepoint || 'N/A';
      if (dateEl) dateEl.textContent = data.date || 'N/A';
      if (countEl) countEl.textContent = `Count: ${data.count || 0}`;
      if (volumeEl) volumeEl.textContent = `Volume: ${data.volume ? data.volume.toFixed(1) : '0.0'} mL`;

      tooltip.style.display = 'block';
      tooltip.style.left = `${event.clientX + 10}px`;
      tooltip.style.top = `${event.clientY - 10}px`;
      tooltip.style.zIndex = '1000';
    } else {
      console.log('Tooltip ref not found');
    }
  };


  // CSV Export function
  const handleExportCSV = () => {
    if (!allScansData || allScansData.length === 0) {
      alert('No data available to export');
      return;
    }

    const sortedScans = allScansData.sort((a, b) => {
      const aTimepoint = parseInt(a.timepoint?.replace('T', '') || '0');
      const bTimepoint = parseInt(b.timepoint?.replace('T', '') || '0');
      return aTimepoint - bTimepoint;
    });

    // Prepare CSV headers
    const headers = ['Biomarker', ...sortedScans.map(scan => {
      const date = scan.analysisResult?.analysis_timestamp ?
        new Date(scan.analysisResult.analysis_timestamp).toLocaleDateString() : '';
      return `${scan.timepoint || 'N/A'}${date ? ` (${date})` : ''}`;
    })];

    // Prepare CSV rows
    const rows = [];

    // Helper function to get biomarker value
    const getBiomarkerValue = (scan, key, isVolume = false) => {
      const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
      const value = biomarkers[key];
      if (value === null || value === undefined || value === '') return 'N/A';
      if (isVolume) {
        return (Number(value) / 1000).toFixed(1);
      }
      return value;
    };

    // Lesion Metrics
    rows.push(['Lesion Metrics', ...Array(sortedScans.length).fill('')]);
    rows.push([
      'Total Lesion Volume (mm³)',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'totalLesionVolume_mm3', true);
        return value !== 'N/A' ? `${value} mL` : 'N/A';
      })
    ]);
    rows.push([
      'Total Lesion Count',
      ...sortedScans.map(scan => getBiomarkerValue(scan, 'totalLesionCount'))
    ]);
    rows.push([
      'Lesion Vol. in White Matter (mm³)',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'lesionVolumeInWhiteMatter_mm3', true);
        return value !== 'N/A' ? `${value} mL` : 'N/A';
      })
    ]);
    rows.push([
      'Lesion Vol. in Grey Matter (mm³)',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'lesionVolumeInGreyMatter_mm3', true);
        return value !== 'N/A' ? `${value} mL` : 'N/A';
      })
    ]);
    rows.push([
      'Average T1 Black Hole Intensity',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'averageT1BlackHoleIntensity');
        return value !== 'N/A' ? Number(value).toFixed(2) : 'N/A';
      })
    ]);

    // Brain Volume Metrics
    rows.push(['Brain Volume Metrics', ...Array(sortedScans.length).fill('')]);
    rows.push([
      'Total Brain Volume (mm³)',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'totalBrainVolume_mm3', true);
        return value !== 'N/A' ? `${value} mL` : 'N/A';
      })
    ]);
    rows.push([
      'Grey Matter Volume (mm³)',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'greyMatterVolume_mm3', true);
        return value !== 'N/A' ? `${value} mL` : 'N/A';
      })
    ]);
    rows.push([
      'White Matter Volume (mm³)',
      ...sortedScans.map(scan => {
        const value = getBiomarkerValue(scan, 'whiteMatterVolume_mm3', true);
        return value !== 'N/A' ? `${value} mL` : 'N/A';
      })
    ]);

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `biomarker_data_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  // Helper function to format biomarker values
  const formatBiomarkerValue = (value, unit = '', decimalPlaces = 2) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return 'N/A';
    }
    return `${numValue.toFixed(decimalPlaces)}${unit}`;
  };

  // Helper function to determine trend direction
  const getTrendDirection = (currentValue, previousValue) => {
    if (!currentValue || !previousValue || currentValue === 'N/A' || previousValue === 'N/A') {
      return 'stable';
    }
    const current = Number(currentValue);
    const previous = Number(previousValue);
    if (isNaN(current) || isNaN(previous)) {
      return 'stable';
    }

    // Calculate percentage change
    const change = ((current - previous) / previous) * 100;
    if (change > 5) return 'increasing';
    if (change < -5) return 'decreasing';
    return 'stable';
  };

  // Helper function to get trend color
  const getTrendColor = (trend, isPositiveTrend = false) => {
    if (trend === 'increasing') {
      return isPositiveTrend ? '#28a745' : '#dc3545';
    }
    if (trend === 'decreasing') {
      return isPositiveTrend ? '#dc3545' : '#28a745';
    }
    return '#6c757d';
  };

  // Helper function to get trend icon
  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return '↗️';
    if (trend === 'decreasing') return '↘️';
    return '→';
  };

  // Helper function to get risk level
  const getRiskLevel = (value, thresholds) => {
    if (value === 'N/A' || value === null || value === undefined) return 'unknown';
    const numValue = Number(value);
    if (isNaN(numValue)) return 'unknown';

    if (numValue >= thresholds.high) return 'high';
    if (numValue >= thresholds.medium) return 'medium';
    return 'low';
  };

  // Helper function to get risk color
  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return '#dc3545';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  // Process longitudinal data for charts
  const processLongitudinalData = () => {
    if (!allScansData || allScansData.length === 0) {
      return {
        lesionCount: [],
        lesionVolume: [],
        brainVolume: [],
        greyMatterVolume: [],
        whiteMatterVolume: [],
        t1Intensity: [],
        gmLesionRatio: [],
        lesionVolumeInGreyMatter: [],
        timepoints: []
      };
    }

    const sortedScans = allScansData.sort((a, b) => {
      const aTimepoint = parseInt(a.timepoint?.replace('T', '') || '0');
      const bTimepoint = parseInt(b.timepoint?.replace('T', '') || '0');
      return aTimepoint - bTimepoint;
    });

    return {
      lesionCount: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.totalLesionCount || 0;
      }),
      lesionVolume: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.totalLesionVolume_mm3 ? Number(biomarkers.totalLesionVolume_mm3) : 0;
      }),
      brainVolume: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.totalBrainVolume_mm3 ? Number(biomarkers.totalBrainVolume_mm3) / 1000 : 0;
      }),
      greyMatterVolume: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.greyMatterVolume_mm3 ? Number(biomarkers.greyMatterVolume_mm3) / 1000 : 0;
      }),
      whiteMatterVolume: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.whiteMatterVolume_mm3 ? Number(biomarkers.whiteMatterVolume_mm3) / 1000 : 0;
      }),
      t1Intensity: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.averageT1BlackHoleIntensity || 0;
      }),
      gmLesionRatio: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        // Check if value exists and is not null/undefined, otherwise return 0
        const ratio = biomarkers.greyMatterLesionRatio;
        return (ratio !== null && ratio !== undefined && ratio !== '') ? Number(ratio) : 0;
      }),
      lesionVolumeInGreyMatter: sortedScans.map(scan => {
        const biomarkers = scan.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
        return biomarkers.lesionVolumeInGreyMatter_mm3 ? Number(biomarkers.lesionVolumeInGreyMatter_mm3) / 1000 : 0;
      }),
      timepoints: sortedScans.map(scan => scan.timepoint || 'N/A')
    };
  };

  const longitudinalData = processLongitudinalData();

  // Helper function to calculate min/max with padding
  // Always starts at 0, max is calculated from data + padding and rounded to nice numbers
  const getMinMax = (dataArray, paddingAmount = null) => {
    if (!dataArray || dataArray.length === 0) {
      return { min: 0, max: 1 };
    }
    const validValues = dataArray.filter(val => val !== null && val !== undefined && val !== '');
    if (validValues.length === 0) {
      // If no valid values, return default
      return { min: 0, max: 1 };
    }
    const max = Math.max(...validValues);
    
    // Calculate padding: if not provided, use percentage of max or fixed amount based on scale
    let padding;
    if (paddingAmount !== null) {
      padding = paddingAmount;
    } else {
      // Auto-calculate padding based on the value scale
      if (max < 100) {
        padding = Math.max(10, max * 0.1); // For small values, use 10% or minimum 10
      } else if (max < 1000) {
        padding = Math.max(50, max * 0.05); // For medium values, use 5% or minimum 50
      } else {
        padding = Math.max(200, max * 0.1); // For large values, use 10% or minimum 200
      }
    }
    
    const maxWithPadding = max + padding;
    
    // Round up to a nice number based on the scale
    let roundedMax;
    if (maxWithPadding < 10) {
      // For values < 10, round up to next integer
      roundedMax = Math.ceil(maxWithPadding);
    } else if (maxWithPadding < 100) {
      // For values < 100, round up to next 5
      roundedMax = Math.ceil(maxWithPadding / 5) * 5;
    } else if (maxWithPadding < 1000) {
      // For values < 1000, round up to next 10
      roundedMax = Math.ceil(maxWithPadding / 10) * 10;
    } else if (maxWithPadding < 10000) {
      // For values < 10000, round up to next 100
      roundedMax = Math.ceil(maxWithPadding / 100) * 100;
    } else {
      // For values >= 10000, round up to next 1000
      roundedMax = Math.ceil(maxWithPadding / 1000) * 1000;
    }
    
    return {
      min: 0,
      max: roundedMax
    };
  };

  // Get current biomarker values
  const currentValues = {
    lesionCount: scanData?.totalLesionCount || 'N/A',
    lesionVolume: scanData?.totalLesionVolume ? Number(scanData.totalLesionVolume) / 1000 : 'N/A',
    brainVolume: scanData?.totalBrainVolume ? Number(scanData.totalBrainVolume) / 1000 : 'N/A',
    greyMatterVolume: scanData?.greyMatterVolume ? Number(scanData.greyMatterVolume) / 1000 : 'N/A',
    whiteMatterVolume: scanData?.whiteMatterVolume ? Number(scanData.whiteMatterVolume) / 1000 : 'N/A',
    t1Intensity: scanData?.whiteMatterIntensity || 'N/A',
    gmLesionRatio: scanData?.greyMatterLesionRatio || 'N/A',
    atrophyRate: scanData?.brainAtrophyRate || scanData?.atrophyRate || 'N/A'
  };

  // Get previous values for trend calculation
  const previousValues = longitudinalData.timepoints.length > 1 ? {
    lesionCount: longitudinalData.lesionCount[longitudinalData.lesionCount.length - 2] || 'N/A',
    lesionVolume: longitudinalData.lesionVolume[longitudinalData.lesionVolume.length - 2] || 'N/A',
    brainVolume: longitudinalData.brainVolume[longitudinalData.brainVolume.length - 2] || 'N/A',
    greyMatterVolume: longitudinalData.greyMatterVolume[longitudinalData.greyMatterVolume.length - 2] || 'N/A',
    whiteMatterVolume: longitudinalData.whiteMatterVolume[longitudinalData.whiteMatterVolume.length - 2] || 'N/A',
    t1Intensity: longitudinalData.t1Intensity[longitudinalData.t1Intensity.length - 2] || 'N/A',
    gmLesionRatio: longitudinalData.gmLesionRatio[longitudinalData.gmLesionRatio.length - 2] || 'N/A'
  } : {};

  // Calculate trends
  const trends = {
    lesionCount: getTrendDirection(currentValues.lesionCount, previousValues.lesionCount),
    lesionVolume: getTrendDirection(currentValues.lesionVolume, previousValues.lesionVolume),
    brainVolume: getTrendDirection(currentValues.brainVolume, previousValues.brainVolume),
    greyMatterVolume: getTrendDirection(currentValues.greyMatterVolume, previousValues.greyMatterVolume),
    whiteMatterVolume: getTrendDirection(currentValues.whiteMatterVolume, previousValues.whiteMatterVolume),
    t1Intensity: getTrendDirection(currentValues.t1Intensity, previousValues.t1Intensity),
    gmLesionRatio: getTrendDirection(currentValues.gmLesionRatio, previousValues.gmLesionRatio)
  };

  // Calculate risk levels
  const riskLevels = {
    lesionVolume: getRiskLevel(currentValues.lesionVolume, { medium: 10, high: 20 }),
    brainAtrophy: getRiskLevel(currentValues.atrophyRate, { medium: 0.5, high: 1.0 }),
    t1Intensity: getRiskLevel(currentValues.t1Intensity, { medium: 500, high: 1000 })
  };

  return (
    <div className='biomarkers-content'>
      {/* Longitudinal Imaging Biomarker Analysis */}
      <div className="custom-card">
        <div className="custom-card-header">
          <div className="custom-card-header-text">
            <h2>
              <div className='icon-wrapper'>
                <img src={Images.Brain} alt="Brain Icon" className="icon" />
              </div>
              Longitudinal Imaging Biomarker Analysis
            </h2>
            <p>MRI-derived biomarkers tracking disease progression over time</p>
          </div>
        </div>
        <div className='biomarkers-cards mb-0 mt-4'>
          {/* Lesion Burden Card */}
          <div className='biomarker-card'>
            <div className='biomarker-card-icon'>
              <img
                src={Images.improving}
                alt="trend"
                className={`trend-icon ${trends.lesionVolume}`}
              />
            </div>
            <div className='biomarker-card-content'>
              <div className='biomarker-card-label'>Lesion Burden</div>
              <div
                className='biomarker-card-status'
                style={{ color: getTrendColor(trends.lesionVolume, false) }}
              >
                {trends.lesionVolume === 'increasing' ? 'Increasing' :
                  trends.lesionVolume === 'decreasing' ? 'Decreasing' : 'Stable'}
              </div>
            </div>
          </div>

          {/* Brain Volume Card */}
          <div className='biomarker-card'>
            <div className='biomarker-card-icon'>
              <img
                src={Images.improving}
                alt="trend"
                className={`trend-icon ${trends.brainVolume}`}
              />
            </div>
            <div className='biomarker-card-content'>
              <div className='biomarker-card-label'>Brain Volume</div>
              <div
                className='biomarker-card-status'
                style={{ color: getTrendColor(trends.brainVolume, false) }}
              >
                {trends.brainVolume === 'increasing' ? 'Increasing' :
                  trends.brainVolume === 'decreasing' ? 'Atrophying' : 'Stable'}
              </div>
            </div>
          </div>

          {/* T1 Black Holes Card */}
          <div className='biomarker-card'>
            <div className='biomarker-card-icon'>
              <img
                src={Images.improving}
                alt="trend"
                className={`trend-icon ${trends.t1Intensity}`}
              />
            </div>
            <div className='biomarker-card-content'>
              <div className='biomarker-card-label'>T1 Black Holes</div>
              <div
                className='biomarker-card-status'
                style={{ color: getTrendColor(trends.t1Intensity, false) }}
              >
                {trends.t1Intensity === 'increasing' ? 'Worsening' :
                  trends.t1Intensity === 'decreasing' ? 'Improving' : 'Stable'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Longitudinal Trend Analysis */}
      <div className='longitudinal-trends'>
        <div className='biomarkers-header d-flex gap-2 mb-2'>
          <h4>Longitudinal Trend Analysis</h4>
        </div>

        {/* Lesion Burden Progression Chart */}
        <div className='chart-container'>
          <div className='chart-header'>
            <h5>Lesion Burden Progression</h5>
            <p>Lesion count and total volume over time</p>
          </div>
          <div className='chart-content'>
            {longitudinalData.timepoints.length > 0 ? (
              <ReactChart
                option={{
                  tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                      type: 'cross'
                    },
                    formatter: function(params) {
                      let result = params[0].name + '<br/>';
                      params.forEach(function(item) {
                        if (item.seriesName === 'Lesion Count') {
                          result += item.marker + item.seriesName + ': ' + item.value + '<br/>';
                        } else {
                          result += item.marker + item.seriesName + ': ' + item.value.toLocaleString() + ' mm³<br/>';
                        }
                      });
                      return result;
                    }
                  },
                  legend: {
                    data: ['Lesion Count', 'Total Volume (mm³)'],
                    bottom: 0
                  },
                  xAxis: [
                    {
                      type: 'category',
                      data: longitudinalData.timepoints,
                      axisPointer: {
                        type: 'shadow'
                      }
                    }
                  ],
                  yAxis: [
                    {
                      type: 'value',
                      name: 'Lesion Count',
                      ...(longitudinalData.lesionCount.length > 0 ? getMinMax(longitudinalData.lesionCount) : { min: 0, max: 20 }),
                      position: 'left',
                      axisLabel: {
                        formatter: '{value}'
                      }
                    },
                    {
                      type: 'value',
                      name: 'Lesion Volume (mm³)',
                      ...(longitudinalData.lesionVolume.length > 0 ? getMinMax(longitudinalData.lesionVolume) : { min: 0, max: 22000 }),
                      position: 'right',
                      axisLabel: {
                        formatter: function(value) {
                          return value.toLocaleString();
                        }
                      }
                    }
                  ],
                  series: [
                    {
                      name: 'Lesion Count',
                      type: 'bar',
                      data: longitudinalData.lesionCount,
                      itemStyle: {
                        color: '#007bff'
                      },
                      label: {
                        show: true,
                        position: 'inside',
                        color: '#fff',
                        fontWeight: 'bold'
                      }
                    },
                    {
                      name: 'Total Volume (mm³)',
                      type: 'line',
                      yAxisIndex: 1,
                      data: longitudinalData.lesionVolume,
                      itemStyle: {
                        color: '#28a745'
                      },
                      symbol: 'circle',
                      symbolSize: 8,
                      lineStyle: {
                        width: 2
                      },
                      label: {
                        show: true,
                        position: 'top',
                        formatter: function(params) {
                          return params.value.toLocaleString();
                        }
                      }
                    }
                  ],
                  grid: {
                    left: '10%',
                    right: '10%',
                    bottom: '15%',
                    top: '10%',
                    containLabel: true
                  }
                }}
                style={{ height: '350px', width: '100%' }}
              />
            ) : (
              <div className='no-data-message'>
                <div className='no-data-icon'>📊</div>
                <h4>No Data Available</h4>
                <p>Lesion burden data will appear here as more scans are processed</p>
              </div>
            )}
          </div>
        </div>


        {/* Brain Volume Atrophy Chart */}
        <div className='chart-container'>
          <div className='chart-header'>
            <h5>Brain Volume Atrophy</h5>
            <p>Total brain, grey matter, and white matter volumes (mL)</p>
          </div>
          <div className='chart-content'>
            {longitudinalData.timepoints.length > 0 ? (
              <ReactChart
                option={{
                  tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                      type: 'cross'
                    }
                  },
                  legend: {
                    data: ['Total brain volume', 'Grey matter volume', 'White matter volume'],
                    bottom: 0
                  },
                  xAxis: {
                    type: 'category',
                    data: longitudinalData.timepoints
                  },
                  yAxis: {
                    type: 'value',
                    name: 'Volume (mL)',
                    ...(longitudinalData.brainVolume.length > 0 || longitudinalData.greyMatterVolume.length > 0 || longitudinalData.whiteMatterVolume.length > 0 
                      ? getMinMax([...longitudinalData.brainVolume, ...longitudinalData.greyMatterVolume, ...longitudinalData.whiteMatterVolume]) 
                      : { min: 700, max: 1400 })
                  },
                  series: [
                    {
                      name: 'Total brain volume',
                      type: 'line',
                      data: longitudinalData.brainVolume,
                      itemStyle: {
                        color: '#007bff'
                      },
                      symbol: 'circle',
                      symbolSize: 8,
                      lineStyle: {
                        width: 2
                      },
                      label: {
                        show: true,
                        position: 'top',
                        formatter: function(params) {
                          return Math.round(params.value);
                        }
                      }
                    },
                    {
                      name: 'Grey matter volume',
                      type: 'line',
                      data: longitudinalData.greyMatterVolume,
                      itemStyle: {
                        color: '#6f42c1'
                      },
                      symbol: 'circle',
                      symbolSize: 8,
                      lineStyle: {
                        width: 2
                      },
                      label: {
                        show: true,
                        position: 'top',
                        formatter: function(params) {
                          return Math.round(params.value);
                        }
                      }
                    },
                    {
                      name: 'White matter volume',
                      type: 'line',
                      data: longitudinalData.whiteMatterVolume,
                      itemStyle: {
                        color: '#28a745'
                      },
                      symbol: 'circle',
                      symbolSize: 8,
                      lineStyle: {
                        width: 2
                      },
                      label: {
                        show: true,
                        position: 'top',
                        formatter: function(params) {
                          return Math.round(params.value);
                        }
                      }
                    }
                  ],
                  grid: {
                    left: '10%',
                    right: '10%',
                    bottom: '15%',
                    top: '10%',
                    containLabel: true
                  }
                }}
                style={{ height: '350px', width: '100%' }}
              />
            ) : (
              <div className='no-data-message'>
                <div className='no-data-icon'>📊</div>
                <h4>No Data Available</h4>
                <p>Brain volume data will appear here as more scans are processed</p>
              </div>
            )}
          </div>
        </div>

        {/* Lesion Characteristics Chart */}
        <div className='chart-container'>
          <div className='chart-header'>
            <h5>Lesion Characteristics</h5>
            <p>T1 black hole intensity and lesion volume in grey matter</p>
          </div>
          <div className='chart-content'>
            {longitudinalData.timepoints.length > 0 ? (
              <ReactChart
                option={{
                  tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                      type: 'cross'
                    },
                    formatter: function(params) {
                      let result = params[0].name + '<br/>';
                      params.forEach(function(item) {
                        if (item.seriesName === 'T1 Black Hole Intensity') {
                          result += item.marker + item.seriesName + ': ' + item.value.toFixed(2) + '<br/>';
                        } else {
                          result += item.marker + item.seriesName + ': ' + item.value.toFixed(1) + ' mL<br/>';
                        }
                      });
                      result += '<span style="font-size: 10px; color: #999; font-style: italic;">Lower T1 intensity = more severe lesions</span>';
                      return result;
                    }
                  },
                  legend: {
                    data: ['T1 Black Hole Intensity', 'Lesion Volume in Grey Matter'],
                    bottom: 0
                  },
                  xAxis: {
                    type: 'category',
                    data: longitudinalData.timepoints
                  },
                  yAxis: [
                    {
                      type: 'value',
                      name: 'T1 Intensity',
                      ...(longitudinalData.t1Intensity.length > 0 ? getMinMax(longitudinalData.t1Intensity) : { min: 0, max: 300 }),
                      position: 'left',
                      axisLabel: {
                        formatter: '{value}'
                      }
                    },
                    {
                      type: 'value',
                      name: 'Lesion Volume in Grey Matter (mL)',
                      ...(longitudinalData.lesionVolumeInGreyMatter.length > 0 
                        ? getMinMax(longitudinalData.lesionVolumeInGreyMatter) 
                        : { min: 0, max: 100 }),
                      position: 'right',
                      axisLabel: {
                        formatter: function(value) {
                          return value.toFixed(1);
                        }
                      }
                    }
                  ],
                  series: [
                    {
                      name: 'T1 Black Hole Intensity',
                      type: 'line',
                      data: longitudinalData.t1Intensity,
                      itemStyle: {
                        color: '#dc3545'
                      },
                      symbol: 'circle',
                      symbolSize: 8,
                      lineStyle: {
                        width: 2
                      },
                      label: {
                        show: true,
                        position: 'top',
                        formatter: function(params) {
                          return params.value.toFixed(2);
                        }
                      }
                    },
                    {
                      name: 'Lesion Volume in Grey Matter',
                      type: 'line',
                      yAxisIndex: 1,
                      data: longitudinalData.lesionVolumeInGreyMatter,
                      itemStyle: {
                        color: '#fd7e14'
                      },
                      symbol: 'circle',
                      symbolSize: 8,
                      lineStyle: {
                        width: 2
                      },
                      label: {
                        show: true,
                        position: 'top',
                        formatter: function(params) {
                          return params.value.toFixed(1);
                        }
                      }
                    }
                  ],
                  grid: {
                    left: '10%',
                    right: '10%',
                    bottom: '15%',
                    top: '10%',
                    containLabel: true
                  }
                }}
                style={{ height: '350px', width: '100%' }}
              />
            ) : (
              <div className='no-data-message'>
                <div className='no-data-icon'>📊</div>
                <h4>No Data Available</h4>
                <p>Lesion characteristics data will appear here as more scans are processed</p>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Biomarker Data Table */}
        <div className='biomarker-table-container'>
          <div className='biomarker-table-header'>
            <div>
              <h5>Detailed Biomarker Data</h5>
              <p>Comprehensive numerical values for all imaging biomarkers</p>
            </div>
            <button className='export-csv-btn' onClick={handleExportCSV}>
              {/* <span className='icon'>📥</span> */}
              Export CSV
            </button>
          </div>
          <div className='biomarker-table-wrapper'>
            <table className='biomarker-table '>
              <thead>
                <tr>
                  <th>Biomarker</th>
                  {longitudinalData.timepoints.map((timepoint, index) => {
                    const date = allScansData[index]?.analysisResult?.analysis_timestamp ?
                      new Date(allScansData[index].analysisResult.analysis_timestamp).toLocaleDateString() : '';
                    return (
                      <th key={index}>
                        <div>{timepoint}</div>
                        {date && <div className='table-date'>{date}</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Lesion Metrics */}
                <tr className='category-row'>
                  <td colSpan={longitudinalData.timepoints.length + 1} className='category-header'>
                    Lesion Metrics
                  </td>
                </tr>
                <tr>
                  <td>Total Lesion Volume (mm³)</td>
                  {longitudinalData.lesionVolume.map((volume, index) => {
                    const volumeInMl = volume ? (Number(volume) / 1000).toFixed(1) : null;
                    return <td key={index}>{volumeInMl ? `${volumeInMl} mL` : 'N/A'}</td>;
                  })}
                </tr>
                <tr>
                  <td>Total Lesion Count</td>
                  {longitudinalData.lesionCount.map((count, index) => (
                    <td key={index}>{count !== null && count !== undefined && count !== '' ? Math.round(Number(count)) : 'N/A'}</td>
                  ))}
                </tr>
                <tr>
                  <td>Lesion Vol. in White Matter (mm³)</td>
                  {longitudinalData.timepoints.map((_, index) => {
                    const biomarkers = allScansData[index]?.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
                    const wmVolume = biomarkers.lesionVolumeInWhiteMatter_mm3 ?
                      (Number(biomarkers.lesionVolumeInWhiteMatter_mm3) / 1000).toFixed(1) : 'N/A';
                    return <td key={index}>{wmVolume !== 'N/A' ? `${wmVolume} mL` : 'N/A'}</td>;
                  })}
                </tr>
                <tr>
                  <td>Lesion Vol. in Grey Matter (mm³)</td>
                  {longitudinalData.timepoints.map((_, index) => {
                    const biomarkers = allScansData[index]?.analysisResult?.consolidated_results?.[0]?.biomarkers || {};
                    const gmVolume = biomarkers.lesionVolumeInGreyMatter_mm3 ?
                      (Number(biomarkers.lesionVolumeInGreyMatter_mm3) / 1000).toFixed(1) : 'N/A';
                    return <td key={index}>{gmVolume !== 'N/A' ? `${gmVolume} mL` : 'N/A'}</td>;
                  })}
                </tr>
                <tr>
                  <td>Average T1 Black Hole Intensity</td>
                  {longitudinalData.t1Intensity.map((intensity, index) => (
                    <td key={index}>{intensity !== null && intensity !== undefined && intensity !== '' && intensity !== 0 ? Number(intensity).toFixed(2) : 'N/A'}</td>
                  ))}
                </tr>

                {/* Brain Volume Metrics */}
                <tr className='category-row'>
                  <td colSpan={longitudinalData.timepoints.length + 1} className='category-header'>
                    Brain Volume Metrics
                  </td>
                </tr>
                <tr>
                  <td>Total Brain Volume (mm³)</td>
                  {longitudinalData.brainVolume.map((volume, index) => (
                    <td key={index}>{volume ? `${volume.toFixed(1)} mL` : 'N/A'}</td>
                  ))}
                </tr>
                <tr>
                  <td>Grey Matter Volume (mm³)</td>
                  {longitudinalData.greyMatterVolume.map((volume, index) => (
                    <td key={index}>{volume ? `${volume.toFixed(1)} mL` : 'N/A'}</td>
                  ))}
                </tr>
                <tr>
                  <td>White Matter Volume (mm³)</td>
                  {longitudinalData.whiteMatterVolume.map((volume, index) => (
                    <td key={index}>{volume ? `${volume.toFixed(1)} mL` : 'N/A'}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

function Biomarkers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [scanData, setScanData] = useState(null);
  const [allScansData, setAllScansData] = useState([]);
  const currentPatientIdRef = useRef(null);

  useEffect(() => {
    // Check for selected patient from sessionStorage (set by eye icon click)
    const checkSelectedPatient = () => {
      const patientData = sessionStorage.getItem('selectedPatient');
      if (patientData) {
        try {
          const patient = JSON.parse(patientData);
          // Only update if patient has changed
          if (currentPatientIdRef.current !== patient.id) {
            currentPatientIdRef.current = patient.id;
            setSelectedPatient(patient);
            fetchPatientScans(patient.id);
          }
        } catch (error) {
          console.error('Error parsing patient data:', error);
          currentPatientIdRef.current = null;
          setSelectedPatient(null);
        }
      } else {
        // Only clear if we had a patient selected
        if (currentPatientIdRef.current !== null) {
          currentPatientIdRef.current = null;
          setSelectedPatient(null);
        }
      }
    };

    // Check initially
    checkSelectedPatient();

    // Listen for patient selection events
    const handlePatientSelected = () => {
      checkSelectedPatient();
    };

    window.addEventListener('patientSelected', handlePatientSelected);

    return () => {
      window.removeEventListener('patientSelected', handlePatientSelected);
    };
  }, []);

  const fetchPatientScans = async (patientId) => {
    try {
      setLoading(true);
      const response = await scanService.getScansByCustomer(patientId);
      if (response.data && response.data.length > 0) {
        const scans = response.data;
        setAllScansData(scans);

        // Get the latest scan data
        const latestScan = scans[0];
        if (latestScan.analysisResult) {
          const analysisResult = latestScan.analysisResult;
          const consolidatedResult = analysisResult.consolidated_results?.[0];
          const biomarkers = consolidatedResult?.biomarkers || {};

          const processedScanData = {
            timepoint: analysisResult.timepoint,
            patientId: analysisResult.patient_id,
            analysisTimestamp: analysisResult.analysis_timestamp,
            processingTime: analysisResult.processing_time_seconds,
            totalLesionCount: biomarkers.totalLesionCount,
            totalLesionVolume: biomarkers.totalLesionVolume_mm3,
            totalBrainVolume: biomarkers.totalBrainVolume_mm3,
            greyMatterVolume: biomarkers.greyMatterVolume_mm3,
            whiteMatterVolume: biomarkers.whiteMatterVolume_mm3,
            whiteMatterLesionVolume: biomarkers.lesionVolumeInWhiteMatter_mm3,
            greyMatterLesionVolume: biomarkers.lesionVolumeInGreyMatter_mm3,
            whiteMatterIntensity: biomarkers.averageT1BlackHoleIntensity,
            lesionLoad: biomarkers.totalLesionVolume_mm3,
            brainAtrophyRate: biomarkers.annualizedBrainVolumeLoss_mm3_per_year ?
              (Number(biomarkers.annualizedBrainVolumeLoss_mm3_per_year) / 1000).toFixed(2) :
              (biomarkers.brainAtrophyRate || biomarkers.atrophyRate || 'N/A'),
            atrophyRate: biomarkers.annualizedBrainVolumeLoss_mm3_per_year ?
              (Number(biomarkers.annualizedBrainVolumeLoss_mm3_per_year) / 1000).toFixed(2) :
              (biomarkers.atrophyRate || biomarkers.brainAtrophyRate || 'N/A'),
            changeFromBaseline_totalLesionVolume: biomarkers.changeFromBaseline_totalLesionVolume_mm3,
            changeFromBaseline_totalLesionCount: biomarkers.changeFromBaseline_totalLesionCount,
            changeFromBaseline_totalBrainVolume: biomarkers.changeFromBaseline_totalBrainVolume_mm3,
            changeFromBaseline_greyMatterVolume: biomarkers.changeFromBaseline_greyMatterVolume_mm3,
            changeFromBaseline_whiteMatterVolume: biomarkers.changeFromBaseline_whiteMatterVolume_mm3,
            changeFromBaseline_averageT1BlackHoleIntensity: biomarkers.changeFromBaseline_averageT1BlackHoleIntensity,
            greyMatterLesionRatio: biomarkers.greyMatterLesionRatio
          };

          setScanData(processedScanData);
        }
      } else {
        setScanData(null);
        setAllScansData([]);
      }
    } catch (error) {
      console.error('Error fetching patient scans:', error);
      setScanData(null);
      setAllScansData([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="biomarkers-page">
        <div className="loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="biomarkers-page">

      {selectedPatient && scanData ? (
        <BiomarkersDisplay scanData={scanData} allScansData={allScansData} />
      ) : selectedPatient ? (
        <div className="no-data-message">
          <div className="no-data-icon">📊</div>
          <h4>No Biomarker Data Available</h4>
          <p>This patient doesn't have any scan data available for biomarker analysis.</p>
        </div>
      ) : (
        <div className="no-data-message">
          <div className="no-data-icon">👤</div>
          <h4>No Patient Selected</h4>
          <p>Please select a patient by clicking the eye icon in the Patient Table to view their biomarker analysis.</p>
        </div>
      )}
    </div>
  );
}

export default Biomarkers;